import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  Button,
  Input,
  Modal,
  Select,
  Textarea,
  type SelectOption,
} from '../../ui';
import {
  GOAL_FRAMEWORKS,
  GOAL_LEVELS,
  GOAL_TYPES,
  PLANNING_PERIODS,
} from '../../features/goals/goalConfig';
import {
  GOAL_STATUSES,
  blankGoalDraft,
  draftFromGoal,
  listToInput,
  normalizeProgress,
  parseList,
  validateGoalDraft,
} from '../../features/goals/goalModel';
import type {
  Goal,
  GoalDraft,
  GoalStatus,
  GoalSummary,
} from '../../features/goals/types';
import { PeopleInput } from '../common/PeopleInput';
import { usePeopleDirectory } from '../../features/teams/usePeopleDirectory';

export interface GoalModalProps {
  open: boolean;
  onClose: () => void;
  /** When set, the modal edits this goal; otherwise it creates a new one. */
  goal?: Goal | null;
  /** Existing goals — used to populate the parent-goal picker. */
  goals: GoalSummary[];
  onCreate: (draft: GoalDraft) => Promise<Goal>;
  onSave?: (draft: GoalDraft) => Promise<Goal>;
}

function toOptions(items: { value: string; label: string }[]): SelectOption[] {
  return items.map((i) => ({ value: i.value, label: i.label }));
}

/**
 * Create / edit form for a Goal. Keeps the "core" fields (title, taxonomy,
 * narrative, status, ownership, next milestone) up top and the traceability /
 * link fields below. Multi-value fields accept comma or newline separated
 * values. Reads/writes only a {@link GoalDraft}; persistence is the caller's.
 */
export function GoalModal({
  open,
  onClose,
  goal,
  goals,
  onCreate,
  onSave,
}: GoalModalProps) {
  const editing = !!goal;
  const { people } = usePeopleDirectory();
  const [draft, setDraft] = useState<GoalDraft>(blankGoalDraft());
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  // Progressive disclosure: only Title + Type + Framework + Status show by
  // default. Editing an existing goal expands advanced fields so its full data
  // is visible; creating a goal starts collapsed to reduce cognitive load.
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Raw text mirrors for the multi-value / date inputs.
  const [pocsText, setPocsText] = useState('');
  const [contributorsText, setContributorsText] = useState('');
  const [dependenciesText, setDependenciesText] = useState('');
  const [risksText, setRisksText] = useState('');
  const [linkedStoriesText, setLinkedStoriesText] = useState('');
  const [linkedInitiativesText, setLinkedInitiativesText] = useState('');
  const [relatedGoalsText, setRelatedGoalsText] = useState('');
  const [milestoneLabel, setMilestoneLabel] = useState('');
  const [milestoneDate, setMilestoneDate] = useState('');
  const [progressText, setProgressText] = useState('');

  useEffect(() => {
    if (!open) return;
    const base = goal ? draftFromGoal(goal) : blankGoalDraft();
    setDraft(base);
    setProblems([]);
    setPocsText(listToInput(base.pocs));
    setContributorsText(listToInput(base.contributors));
    setDependenciesText(listToInput(base.dependencies));
    setRisksText(listToInput(base.risks));
    setLinkedStoriesText(listToInput(base.linkedStoryIds));
    setLinkedInitiativesText(listToInput(base.linkedInitiativeIds));
    setRelatedGoalsText(listToInput(base.relatedGoalIds));
    setMilestoneLabel(base.nextMilestone?.label ?? '');
    setMilestoneDate(base.nextMilestone?.dueDate ?? '');
    setProgressText(
      base.manualProgress === undefined ? '' : String(base.manualProgress),
    );
    // Expand advanced fields when editing so existing data is visible.
    setShowAdvanced(!!goal);
  }, [open, goal]);

  const parentOptions = useMemo<SelectOption[]>(
    () =>
      goals
        .filter((g) => g.id !== goal?.id)
        .map((g) => ({ value: g.id, label: g.title || g.id })),
    [goals, goal],
  );

  const set = <K extends keyof GoalDraft>(key: K, value: GoalDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const buildDraft = (): GoalDraft => {
    const label = milestoneLabel.trim();
    const date = milestoneDate.trim();
    return {
      ...draft,
      title: draft.title.trim(),
      manualProgress: normalizeProgress(progressText),
      pocs: parseList(pocsText),
      contributors: parseList(contributorsText),
      dependencies: parseList(dependenciesText),
      risks: parseList(risksText),
      linkedStoryIds: parseList(linkedStoriesText),
      linkedInitiativeIds: parseList(linkedInitiativesText),
      relatedGoalIds: parseList(relatedGoalsText),
      nextMilestone: label
        ? { label, ...(date ? { dueDate: date } : {}) }
        : undefined,
    };
  };

  const submit = async () => {
    const next = buildDraft();
    const found = validateGoalDraft(next);
    if (found.length > 0) {
      setProblems(found);
      return;
    }
    setSaving(true);
    try {
      if (editing && onSave) await onSave(next);
      else await onCreate(next);
      onClose();
    } catch (e) {
      setProblems([e instanceof Error ? e.message : 'Failed to save goal.']);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !saving && onClose()}
      size="xl"
      title={editing ? 'Edit goal' : 'New goal'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button loading={saving} onClick={submit}>
            {editing ? 'Save changes' : 'Create goal'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {problems.length > 0 && (
          <ul className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {problems.map((p) => (
              <li key={p}>• {p}</li>
            ))}
          </ul>
        )}

        <Input
          label="Title"
          placeholder="e.g. Cut checkout p99 latency below 200ms"
          value={draft.title}
          onChange={(e) => set('title', e.target.value)}
        />

        {/* Basics — the minimum needed to create a goal */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Select
            label="Type"
            value={draft.goalType}
            options={toOptions(GOAL_TYPES)}
            onChange={(e) => set('goalType', e.target.value)}
          />
          <Select
            label="Framework"
            value={draft.goalFramework}
            options={toOptions(GOAL_FRAMEWORKS)}
            onChange={(e) => set('goalFramework', e.target.value)}
          />
          <Select
            label="Status"
            value={draft.status}
            options={GOAL_STATUSES.map((s) => ({
              value: s.value,
              label: s.label,
            }))}
            onChange={(e) => set('status', e.target.value as GoalStatus)}
          />
        </div>

        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          aria-expanded={showAdvanced}
          className="flex items-center gap-1.5 text-sm font-medium text-zinc-400 transition-colors hover:text-zinc-200"
        >
          {showAdvanced ? (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          )}
          {showAdvanced
            ? 'Hide details & traceability'
            : 'Add details & traceability'}
        </button>

        {showAdvanced && (
          <div className="space-y-5 border-t border-surface-border pt-5">
            {/* Classification */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Select
                label="Planning period"
                value={draft.planningPeriod}
                options={toOptions(PLANNING_PERIODS)}
                onChange={(e) => set('planningPeriod', e.target.value)}
              />
              <Select
                label="Hierarchy level"
                value={draft.level}
                options={toOptions(GOAL_LEVELS)}
                onChange={(e) => set('level', e.target.value)}
              />
            </div>

        {/* Narrative */}
        <Textarea
          label="Description"
          rows={2}
          value={draft.description}
          onChange={(e) => set('description', e.target.value)}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Textarea
            label="Why it matters"
            rows={2}
            value={draft.whyItMatters}
            onChange={(e) => set('whyItMatters', e.target.value)}
          />
          <Textarea
            label="Expected outcome"
            rows={2}
            value={draft.expectedOutcome}
            onChange={(e) => set('expectedOutcome', e.target.value)}
          />
          <Textarea
            label="Success metrics"
            rows={2}
            value={draft.successMetrics}
            onChange={(e) => set('successMetrics', e.target.value)}
          />
          <Input
            label="Target"
            placeholder="e.g. p99 < 200ms in all regions"
            value={draft.target}
            onChange={(e) => set('target', e.target.value)}
          />
        </div>

        {/* Progress + status note */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Manual progress (%)"
            type="number"
            min={0}
            max={100}
            placeholder="optional"
            value={progressText}
            onChange={(e) => setProgressText(e.target.value)}
          />
          <Input
            label="Status note (override rationale)"
            placeholder="optional"
            value={draft.statusNote ?? ''}
            onChange={(e) => set('statusNote', e.target.value)}
          />
        </div>

        {/* Ownership */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            label="Owning team"
            value={draft.owningTeam}
            onChange={(e) => set('owningTeam', e.target.value)}
          />
          <PeopleInput
            label="Goal lead (alias)"
            value={draft.goalLead}
            people={people}
            onChange={(alias) => set('goalLead', alias)}
          />
          <Input
            label="Leadership sponsor"
            value={draft.leadershipSponsor}
            onChange={(e) => set('leadershipSponsor', e.target.value)}
          />
          <Select
            label="Parent goal"
            placeholder="None (root goal)"
            value={draft.parentGoalId ?? ''}
            options={parentOptions}
            onChange={(e) =>
              set('parentGoalId', e.target.value || undefined)
            }
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="POCs (comma separated)"
            value={pocsText}
            onChange={(e) => setPocsText(e.target.value)}
          />
          <Input
            label="Contributors (comma separated)"
            value={contributorsText}
            onChange={(e) => setContributorsText(e.target.value)}
          />
        </div>

        {/* Next milestone */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Next milestone"
            placeholder="e.g. Beta launch"
            value={milestoneLabel}
            onChange={(e) => setMilestoneLabel(e.target.value)}
          />
          <Input
            label="Milestone due date"
            type="date"
            value={milestoneDate}
            onChange={(e) => setMilestoneDate(e.target.value)}
          />
        </div>

        {/* Traceability links */}
        <Input
          label="Linked story ids — Task ids (comma separated)"
          hint="Engineering work traced to this goal; drives the traceability rollup."
          value={linkedStoriesText}
          onChange={(e) => setLinkedStoriesText(e.target.value)}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Linked initiative ids (comma separated)"
            value={linkedInitiativesText}
            onChange={(e) => setLinkedInitiativesText(e.target.value)}
          />
          <Input
            label="Related goal ids (comma separated)"
            value={relatedGoalsText}
            onChange={(e) => setRelatedGoalsText(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Textarea
            label="Dependencies (one per line)"
            rows={2}
            value={dependenciesText}
            onChange={(e) => setDependenciesText(e.target.value)}
          />
          <Textarea
            label="Risks (one per line)"
            rows={2}
            value={risksText}
            onChange={(e) => setRisksText(e.target.value)}
          />
        </div>
        <Input
          label="Supporting doc link"
          placeholder="https://…"
          value={draft.supportingDocLink ?? ''}
          onChange={(e) => set('supportingDocLink', e.target.value)}
        />
          </div>
        )}
      </div>
    </Modal>
  );
}

export default GoalModal;
